"use client";
// ════════════════════════════════════════════════════════════════════════════
// <ChiKhiCoQuyen perm="..."> — CHỈ hiện nút/ô nhập khi vai ĐANG ĐĂNG NHẬP thật sự
// bấm được. Dùng chung cho MỌI màn (server lẫn client component).
//
// 🩺 BỆNH ĐANG CHỮA (đo 29/07/2026 — 50 màn · 149 lối):
//   Cổng vào TRANG gác bằng quyền ĐỌC (`can("inventory:read")`), nhưng nút trên trang
//   gọi server action gác bằng quyền GHI (`requirePermission("inventory:write")`).
//   ⇒ Bác sĩ vào được /inventory/items, THẤY nút Xoá, BẤM LÀ LỖI — mà `requirePermission`
//   ném Error trần nên bản thật che mất, người dùng chỉ thấy màn đỏ "Đã có lỗi xảy ra".
//   Cùng họ bệnh đã ghi ở `lib/rbac/care-perms.ts` ("cổng ở TRANG khác cổng ở HÀM", 17/07)
//   và ở `components/KhongDuQuyen.tsx` (29/07 — chặn ĐỌC mà ném lỗi thì trông như app vỡ).
//
// 🔒 ĐÂY KHÔNG PHẢI RANH GIỚI BẢO MẬT. Chặn thật vẫn nằm ở `requirePermission()` đầu mỗi
//   server action (ranh giới duy nhất — xem lib/rbac/guard.ts). Thẻ này chỉ lo phần NHÌN:
//   đừng mời người ta bấm cái họ không bấm được. Bỏ thẻ này đi thì app kém lịch sự chứ
//   KHÔNG hở quyền — nên nó được phép "ngã về phía hiện" (xem dưới).
//
// ⚖️ NGÃ VỀ PHÍA HIỆN khi CHƯA BIẾT quyền (perms = null):
//   `app/layout.tsx` lấy quyền trong try/catch fail-open — rớt mạng/DB là `userPerms` rỗng.
//   Nếu coi "rỗng" = "không có quyền" thì MỘT cú lỗi lấy quyền sẽ giấu sạch nút của
//   MỌI người, kể cả Chủ ⇒ tính năng làm-đẹp lại thành sự cố ngừng việc.
//   Vì vậy: null = "chưa biết" → hiện y như trước khi có thẻ này (không tệ hơn hôm nay).
//           [] hoặc mảng thật = "biết rồi" → gác đúng quyền.
// ════════════════════════════════════════════════════════════════════════════
import * as React from "react";

const QuyenCtx = React.createContext<ReadonlySet<string> | null>(null);

export function QuyenProvider({
  perms,
  children,
}: {
  perms: string[] | null;
  children: React.ReactNode;
}) {
  // useMemo theo NỘI DUNG (đã nối chuỗi) — layout render lại tạo mảng mới mỗi lần,
  // so sánh tham chiếu sẽ dựng Set lại vô ích ở mọi lần chuyển trang.
  const khoa = perms ? perms.join("|") : null;
  const bo = React.useMemo(() => (khoa === null ? null : new Set(khoa ? khoa.split("|") : [])), [khoa]);
  return <QuyenCtx.Provider value={bo}>{children}</QuyenCtx.Provider>;
}

/** Vai hiện tại có quyền này không? CHƯA BIẾT (ngoài provider / layout lỗi) → `true` (hiện như cũ). */
export function useCoQuyen(perm: string): boolean {
  const bo = React.useContext(QuyenCtx);
  return bo === null ? true : bo.has(perm);
}

/**
 * Bọc quanh nút/form GHI. `perm` phải TRÙNG chuỗi quyền mà server action đòi
 * (lưới chặn #11 `test/rbac-cong-trang-vs-ham.test.ts` canh cho khỏi lệch).
 *
 *   <ChiKhiCoQuyen perm="inventory:write"><DeleteButton …/></ChiKhiCoQuyen>
 *
 * `thay`: thứ hiện khi KHÔNG đủ quyền (mặc định: không hiện gì). Dùng khi giấu trơn
 * làm hỏng bố cục hoặc khiến người dùng tưởng màn bị lỗi.
 */
export function ChiKhiCoQuyen({
  perm,
  children,
  thay = null,
}: {
  perm: string;
  children: React.ReactNode;
  thay?: React.ReactNode;
}) {
  return <>{useCoQuyen(perm) ? children : thay}</>;
}

export default ChiKhiCoQuyen;
